import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';

import Layout from '../../Layout/Layout';
import GridPreview from './components/GridPreview';
import WallPreviewRenderer from './preview/WallPreviewRenderer';
import { buildTemplatePreviewPayload } from './preview/buildTemplatePreviewPayload';
import { useGetTemplate, useUpdateTemplate, useGetThemes } from '../../../queries/signageTemplates';
import { useGetMenus, useGetMenu } from '../../../queries/signageMenus';
import { useGetWallMediaLibrary } from '../../../queries/videoWall';
import useEditorDirty from './useEditorDirty';

// Persisted content — shared by save() and the dirty serializer so Save enables
// only on a real change.
const serializeTemplate = (s) => JSON.stringify({
  name: (s.name || '').trim(),
  type: s.type,
  theme_key: s.themeKey,
  menu_id: s.menuId || null,
  screen_assignments: Object.keys(s.assignments || {}).length ? s.assignments : null,
  settings: {
    rotation_mode: s.rotationMode,
    rotation_interval_ms: s.rotationInterval,
    logo_url: (s.logoUrl || '').trim() || null,
    orientation: s.orientation || 'landscape',
    editor_grid: { rows: s.rows, cols: s.cols },
  },
});

const TYPES = ['menu', 'special'];
const ROTATION_MODES = ['none', 'rotate'];
const LAYOUTS = [
  { key: '1x1', label: '1 × 1', rows: 1, cols: 1 },
  { key: '1x2', label: '1 × 2', rows: 1, cols: 2 },
  { key: '2x1', label: '2 × 1', rows: 2, cols: 1 },
  { key: '2x2', label: '2 × 2', rows: 2, cols: 2 },
  { key: '1x3', label: '1 × 3', rows: 1, cols: 3 },
  { key: '3x1', label: '3 × 1', rows: 3, cols: 1 },
];

/**
 * Smart Template Engine — template editor with PER-SCREEN section assignment
 * (Phase 3). Pick a layout, then tap each cell to choose which menu sections it
 * shows; saved as screen_assignments {grid_position => [section_id]}. The Android
 * TemplateView renders per_screen[gridPosition] natively, no split/sync.
 * (.claude/TEMPLATE_ENGINE_SPEC.md §2)
 */
export default function VideoWallTemplateEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams();

  const { data: tpl, isLoading } = useGetTemplate(templateId);
  const update = useUpdateTemplate();
  const { data: menus = [] } = useGetMenus();
  const { data: themeData } = useGetThemes();
  const themes = themeData?.themes ?? [];
  const defaultZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const defaultZonesPortrait = useMemo(() => themeData?.defaultZonesPortrait ?? themeData?.defaultZones ?? {}, [themeData]);
  const { data: mediaRaw } = useGetWallMediaLibrary(true);

  const [name, setName] = useState('');
  const [type, setType] = useState('menu');
  const [themeKey, setThemeKey] = useState('noir_gold');
  const [menuId, setMenuId] = useState(null);
  const [rows, setRows] = useState(1);
  const [cols, setCols] = useState(1);
  const [assignments, setAssignments] = useState({}); // { pos: [sectionId] }
  const [rotationMode, setRotationMode] = useState('none');
  const [rotationInterval, setRotationInterval] = useState(8000);
  const [logoUrl, setLogoUrl] = useState('');
  const [orientation, setOrientation] = useState('landscape'); // designed-for shape (WYSIWYG)
  const [cellPos, setCellPos] = useState(null);
  const [gridMode, setGridMode] = useState('preview'); // 'preview' | 'assign'
  const { markClean, isDirty } = useEditorDirty();

  // Resolve a menu item's photo media id → URL for the live preview.
  const resolvePhoto = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    const map = {};
    (Array.isArray(arr) ? arr : []).forEach((m) => { map[m.id] = m.path || m.thumb || null; });
    return (id) => map[id] || null;
  }, [mediaRaw]);

  // The live menu being assigned (re-fetches when the menu dropdown changes).
  const { data: menuFull } = useGetMenu(menuId, !!menuId);
  const sections = useMemo(() => menuFull?.sections ?? [], [menuFull]);
  const sectionName = (s) => s.name_en || s.name_ar || t('video_wall.templates.untitled_section');
  const sectionById = useMemo(() => {
    const m = {}; sections.forEach((s) => { m[s.id] = s; }); return m;
  }, [sections]);

  useEffect(() => {
    if (!tpl) return;
    const sa = tpl.screen_assignments || {};
    const st = tpl.settings || {};
    const g = st.editor_grid || {};
    const loaded = {
      name: tpl.name || '',
      type: tpl.type || 'menu',
      themeKey: tpl.theme_key || 'noir_gold',
      menuId: tpl.menu_id || null,
      assignments: Object.fromEntries(Object.entries(sa).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])),
      rotationMode: st.rotation_mode || 'none',
      rotationInterval: st.rotation_interval_ms || 8000,
      logoUrl: st.logo_url || '',
      orientation: st.orientation === 'portrait' ? 'portrait' : 'landscape',
      rows: g.rows || 1,
      cols: g.cols || 1,
    };
    setName(loaded.name);
    setType(loaded.type);
    setThemeKey(loaded.themeKey);
    setMenuId(loaded.menuId);
    setAssignments(loaded.assignments);
    setRotationMode(loaded.rotationMode);
    setRotationInterval(loaded.rotationInterval);
    setLogoUrl(loaded.logoUrl);
    setOrientation(loaded.orientation);
    setRows(loaded.rows);
    setCols(loaded.cols);
    markClean(serializeTemplate(loaded));
  }, [tpl, markClean]);

  const theme = themes.find((th) => th.key === themeKey);
  const grad = theme?.gradient || ['#1A1A1A', '#000000'];

  // Default zones for THIS template's designed orientation (WYSIWYG preview) — the
  // same selection the backend makes when building the play payload.
  const effectiveDefaultZones = orientation === 'portrait' ? defaultZonesPortrait : defaultZones;

  // Live preview payload — same shape the Android player consumes (single source
  // of truth). Rebuilds on every edit so the preview is instant.
  const previewTemplate = useMemo(() => buildTemplatePreviewPayload({
    instance: {
      type,
      theme_key: themeKey,
      screen_assignments: Object.keys(assignments).length ? assignments : null,
      settings: { rotation_mode: rotationMode, rotation_interval_ms: rotationInterval, logo_url: logoUrl.trim() || null },
    },
    sections,
    theme: theme || {},
    defaultZones: effectiveDefaultZones,
    currency: menuFull?.currency || 'SAR',
    resolvePhoto,
  }), [type, themeKey, assignments, rotationMode, rotationInterval, logoUrl, sections, theme, effectiveDefaultZones, menuFull, resolvePhoto]);

  const setLayout = (l) => {
    setRows(l.rows); setCols(l.cols);
    // Drop assignments for cells that no longer exist.
    const max = l.rows * l.cols;
    setAssignments((a) => Object.fromEntries(Object.entries(a).filter(([k]) => Number(k) <= max)));
  };

  const toggleSectionOnCell = (pos, sectionId) => setAssignments((a) => {
    const cur = a[pos] || [];
    const next = cur.includes(sectionId) ? cur.filter((x) => x !== sectionId) : [...cur, sectionId];
    const copy = { ...a };
    if (next.length) copy[pos] = next; else delete copy[pos];
    return copy;
  });

  const allPositions = useMemo(
    () => Array.from({ length: rows * cols }, (_, i) => i + 1),
    [rows, cols],
  );

  // Scenario B — "Desserts on ALL screens": add one section to every cell at once.
  const addSectionToAll = (sectionId) => {
    if (!sectionId) return;
    setAssignments((a) => {
      const copy = { ...a };
      allPositions.forEach((pos) => {
        const cur = copy[pos] || [];
        if (!cur.includes(sectionId)) copy[pos] = [...cur, sectionId];
      });
      return copy;
    });
  };

  // Copy one cell's exact section set to every screen (quick "make all the same").
  const copyCellToAll = (pos) => setAssignments((a) => {
    const src = a[pos] || [];
    const copy = { ...a };
    allPositions.forEach((p) => { if (src.length) copy[p] = [...src]; else delete copy[p]; });
    return copy;
  });

  const clearAllAssignments = () => setAssignments({});

  const cellLabel = (pos) => {
    const ids = assignments[pos] || [];
    if (!ids.length) return null;
    return ids.map((id) => sectionById[id] ? sectionName(sectionById[id]) : '·').join(', ');
  };

  // Surface the player's behaviour so the editor is honest about what will show:
  //  • screens left empty fall back to Screen 1's content (Android perScreen["1"]).
  //  • a screen with >1 section ROTATES (needs rotation enabled).
  const hasUnassigned = allPositions.some((pos) => !(assignments[pos] || []).length) && (assignments[1] || []).length > 0;
  const multiSectionCells = allPositions.filter((pos) => (assignments[pos] || []).length > 1).length;
  const [quickSection, setQuickSection] = useState(null);

  const serialized = useMemo(
    () => serializeTemplate({ name, type, themeKey, menuId, assignments, rotationMode, rotationInterval, logoUrl, orientation, rows, cols }),
    [name, type, themeKey, menuId, assignments, rotationMode, rotationInterval, logoUrl, orientation, rows, cols],
  );
  const dirty = isDirty(serialized);

  const save = async () => {
    if (!name.trim()) return;
    await update.mutateAsync({
      id: templateId,
      name: name.trim(),
      type,
      theme_key: themeKey,
      menu_id: menuId,
      screen_assignments: Object.keys(assignments).length ? assignments : null,
      settings: {
        rotation_mode: rotationMode,
        rotation_interval_ms: rotationInterval,
        logo_url: logoUrl.trim() || null,
        orientation,
        editor_grid: { rows, cols },
      },
    });
    markClean(serialized); // saved → button disables again
  };

  if (isLoading) return <Layout><Skeleton height="22rem" /></Layout>;
  if (!tpl) return <Layout><div className="pp-card p-5 text-center">{t('video_wall.templates.not_found')}</div></Layout>;

  return (
    <Layout>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <Button text icon="pi pi-arrow-left" label={t('video_wall.templates.back')}
          onClick={() => navigate('/apps/video-wall/templates')} className="p-0" />
        <Button label={t('video_wall.library.save')} icon="pi pi-check" onClick={save}
          loading={update.isLoading} disabled={!name.trim() || !dirty} />
      </div>

      <div className="row g-4">
        {/* Left: settings */}
        <div className="col-12 col-lg-5">
          <div className="pp-card p-4">
            <div className="mb-3">
              <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.name')}</label>
              <InputText className="w-100" value={name} onChange={(e) => setName(e.target.value)} maxLength={150} />
            </div>
            <div className="row g-3">
              <div className="col-6">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.type')}</label>
                <Dropdown className="w-100" value={type} onChange={(e) => setType(e.value)}
                  options={TYPES.map((c) => ({ label: t(`video_wall.templates.type_${c}`), value: c }))} />
              </div>
              <div className="col-12">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.theme')}</label>
                <div className="d-flex flex-wrap gap-2">
                  {themes.map((th) => {
                    const g = th.gradient || ['#1A1A1A', '#000000'];
                    const selected = th.key === themeKey;
                    return (
                      <button key={th.key} type="button" onClick={() => setThemeKey(th.key)}
                        title={th.label}
                        className={`pp-swatch${selected ? ' is-selected' : ''}`}
                        style={{ width: 84, padding: 3 }}>
                        <div style={{ height: 34, borderRadius: 7, background: `linear-gradient(to bottom, ${g[0]}, ${g[g.length - 1]})` }} />
                        <div className="small text-truncate mt-1" style={{ color: selected ? 'var(--pp-text-primary)' : 'var(--pp-text-secondary)' }}>{th.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-12">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.menu')}</label>
                <Dropdown className="w-100" value={menuId} onChange={(e) => setMenuId(e.value)} showClear filter
                  options={menus.map((m) => ({ label: m.name, value: m.id }))} placeholder={t('video_wall.templates.pick_menu')} />
              </div>
              <div className="col-12">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.logo_url')}</label>
                <InputText className="w-100" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://…" />
              </div>
              <div className="col-6">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.rotation')}</label>
                <Dropdown className="w-100" value={rotationMode} onChange={(e) => setRotationMode(e.value)}
                  options={ROTATION_MODES.map((c) => ({ label: t(`video_wall.templates.rotation_${c}`), value: c }))} />
              </div>
              <div className="col-6">
                <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>{t('video_wall.templates.rotation_interval')}</label>
                <InputNumber className="w-100" value={rotationInterval} disabled={rotationMode !== 'rotate'}
                  onValueChange={(e) => setRotationInterval(e.value || 8000)} min={2000} max={60000} step={1000}
                  suffix=" ms" useGrouping={false} />
              </div>
            </div>
          </div>
        </div>

        {/* Right: layout + per-screen assignment */}
        <div className="col-12 col-lg-7">
          <div className="pp-card p-4">
            <h6 className="mb-2" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.templates.layout')}</h6>
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.templates.orientation')}</span>
              <Button size="small" icon="pi pi-window-maximize" label={t('video_wall.templates.landscape')}
                outlined={orientation !== 'landscape'} onClick={() => setOrientation('landscape')} />
              <Button size="small" icon="pi pi-mobile" label={t('video_wall.templates.portrait')}
                outlined={orientation !== 'portrait'} onClick={() => setOrientation('portrait')} />
            </div>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {LAYOUTS.map((l) => (
                <Button key={l.key} label={l.label} size="small" outlined={!(l.rows === rows && l.cols === cols)}
                  onClick={() => setLayout(l)} />
              ))}
            </div>

            <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
              <p className="mb-0 small" style={{ color: 'var(--pp-text-secondary)' }}>
                {gridMode === 'assign'
                  ? (rows * cols > 1 ? t('video_wall.templates.assign_hint') : t('video_wall.templates.assign_hint_single'))
                  : t('video_wall.templates.preview_hint')}
              </p>
              <span className="d-inline-flex gap-1">
                <Button size="small" icon="pi pi-eye" label={t('video_wall.templates.mode_preview')}
                  outlined={gridMode !== 'preview'} onClick={() => setGridMode('preview')} />
                <Button size="small" icon="pi pi-th-large" label={t('video_wall.templates.mode_assign')}
                  outlined={gridMode !== 'assign'} onClick={() => setGridMode('assign')} />
              </span>
            </div>

            {gridMode === 'preview' ? (
              !menuId ? (
                <div className="pp-card p-4 text-center text-muted">{t('video_wall.templates.pick_menu_first')}</div>
              ) : (
                <WallPreviewRenderer template={previewTemplate} grid={{ rows, cols, orientation }} maxWidth={460} />
              )
            ) : !menuId ? (
              <div className="pp-card p-4 text-center text-muted">{t('video_wall.templates.pick_menu_first')}</div>
            ) : (
              <>
                <div style={{ background: `linear-gradient(to bottom, ${grad[0]}, ${grad[grad.length - 1]})`, padding: 16, borderRadius: 12 }}>
                  <GridPreview
                    rows={rows}
                    columns={cols}
                    orientation={orientation}
                    maxWidth={420}
                    renderCell={(r, c, pos) => {
                      const ids = assignments[pos] || [];
                      const label = cellLabel(pos);
                      const rotates = ids.length > 1 && rotationMode === 'rotate';
                      return (
                        <div onClick={() => setCellPos(pos)} title={t('video_wall.templates.assign_cell', { n: pos })}
                          // dir="auto" — the GRID is pinned LTR for physical position; let the
                          // cell's own text (Arabic section names) pick its natural direction.
                          dir="auto"
                          style={{ width: '100%', height: '100%', cursor: 'pointer', padding: 4, textAlign: 'center',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                            fontSize: 11, lineHeight: 1.2, color: label ? '#fff' : 'rgba(255,255,255,0.6)' }}>
                          <span style={{ fontWeight: 700, opacity: 0.7 }}>{t('video_wall.templates.cell')} {pos}</span>
                          <span>{label || t('video_wall.templates.cell_empty')}</span>
                          {ids.length > 1 && (
                            <span className="small" style={{ opacity: 0.8 }}>
                              <i className={rotates ? 'pi pi-sync' : 'pi pi-exclamation-circle'} style={{ fontSize: 9 }} />{' '}
                              {ids.length}{rotates ? '' : '!'}
                            </span>
                          )}
                        </div>
                      );
                    }}
                    cellStyle={() => ({ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.25)' })}
                  />
                </div>

                {/* Quick assign — Scenario B: put one section on every screen in one click. */}
                <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
                  <Dropdown value={quickSection} onChange={(e) => setQuickSection(e.value)} showClear filter
                    options={sections.map((s) => ({ label: sectionName(s), value: s.id }))}
                    placeholder={t('video_wall.templates.quick_pick_section')} style={{ minWidth: 200 }} />
                  <Button size="small" icon="pi pi-clone" label={t('video_wall.templates.add_to_all')}
                    disabled={!quickSection} onClick={() => addSectionToAll(quickSection)} />
                  <Button size="small" text severity="danger" icon="pi pi-times" label={t('video_wall.templates.clear_all')}
                    disabled={!Object.keys(assignments).length} onClick={clearAllAssignments} className="ms-auto" />
                </div>

                {/* Honest behaviour hints */}
                {hasUnassigned && (
                  <p className="small mb-0 mt-2" style={{ color: 'var(--pp-text-secondary)' }}>
                    <i className="pi pi-info-circle me-1" />{t('video_wall.templates.fallback_hint')}
                  </p>
                )}
                {multiSectionCells > 0 && rotationMode !== 'rotate' && (
                  <div className="small mt-2 d-flex align-items-center gap-2 flex-wrap" style={{ color: 'var(--pp-warning, #f59e0b)' }}>
                    <span><i className="pi pi-exclamation-triangle me-1" />{t('video_wall.templates.multi_needs_rotation')}</span>
                    <Button size="small" icon="pi pi-sync" label={t('video_wall.templates.enable_rotation')}
                      onClick={() => setRotationMode('rotate')} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Per-cell section picker */}
      <Dialog visible={cellPos != null} onHide={() => setCellPos(null)}
        header={t('video_wall.templates.assign_cell', { n: cellPos })} style={{ width: 'min(94vw, 440px)' }}>
        {!menuId ? (
          <p className="text-muted">{t('video_wall.templates.pick_menu_first')}</p>
        ) : sections.length === 0 ? (
          <p className="text-muted">{t('video_wall.templates.menu_empty')}</p>
        ) : (
          <div className="d-flex flex-column gap-2 pt-2">
            {sections.map((s) => {
              const checked = (assignments[cellPos] || []).includes(s.id);
              return (
                <label key={s.id} className="d-flex align-items-center gap-2 p-2"
                  style={{ cursor: 'pointer', borderRadius: 8, background: checked ? 'var(--pp-bg-tertiary)' : 'transparent' }}>
                  <Checkbox checked={checked} onChange={() => toggleSectionOnCell(cellPos, s.id)} />
                  <span style={{ color: 'var(--pp-text-primary)' }}>{sectionName(s)}</span>
                  <span className="ms-auto small text-muted">{t('video_wall.menus.items_n', { n: (s.items || []).length })}</span>
                </label>
              );
            })}
            {(assignments[cellPos] || []).length > 1 && (
              <p className="small mb-0 mt-1" style={{ color: 'var(--pp-text-secondary)' }}>
                <i className="pi pi-info-circle me-1" />{t('video_wall.templates.multi_section_note')}
              </p>
            )}
            {rows * cols > 1 && (
              <div className="d-flex justify-content-end gap-2 pt-2 mt-1" style={{ borderTop: '1px solid var(--pp-border)' }}>
                <Button size="small" text icon="pi pi-clone" label={t('video_wall.templates.apply_to_all')}
                  disabled={!(assignments[cellPos] || []).length} onClick={() => { copyCellToAll(cellPos); setCellPos(null); }} />
                <Button size="small" label={t('video_wall.templates.done')} onClick={() => setCellPos(null)} />
              </div>
            )}
          </div>
        )}
      </Dialog>
    </Layout>
  );
}
